import * as _ from 'lodash';

import adVariantIcon from '@/assets/icons/vn-icon-ad-variant.svg';
import clipGroupIcon from '@/assets/icons/vn-icon-clip-group.svg';
import externalClipIcon from '@/assets/icons/vn-icon-external-clip.svg';
import imageIcon from '@/assets/icons/vn-icon-image.svg';
import liveEventIcon from '@/assets/icons/vn-icon-live-event.svg';
import liveProgramAssetIcon from '@/assets/icons/vn-icon-live-program-asset.svg';
import nestedChannelIcon from '@/assets/icons/vn-icon-nested-collection.svg';
import transitionIcon from '@/assets/icons/vn-icon-transition.svg';
import {
  AccessLevelType,
  type CommonBusinessMetadata,
  type CommonTagKeys,
  GeographicRestrictionType,
  LinkedAssetType,
} from '@/models/channel';
import type { OU } from '@/models/Ou';

import type { ISupportedVariantList } from '../constants/supportedVariants';
import { AssetSource, ChannelAttributeType, JobStatus } from '../enums';
import type { Category, ChannelAttribute, Item } from '../models';
import type { LinkedFileInfo } from '../models/item';
import { FuelEntityType } from '../services';
import { SchemaType } from '../store/forms/Asset-Editor/SchemaTypeOptions';
import clientUtils from './clientUtils';
import { dateUtils } from './dateUtils';

export const DEFAULT_FRAME_RATE = 30;

const itemUtils = {
  areArraysEqual(arr1: unknown[], arr2: unknown[]) {
    if ((arr1 == null && arr2 != null) || (arr1 != null && arr2 == null)) {
      return false;
    }
    if (arr1 == null && arr2 == null) {
      return true;
    }
    // First, check if the lengths are different. If so, they cannot be equal.
    if (arr1?.length !== arr2?.length) {
      return false;
    }
    // Iterate through the arrays, comparing elements at each index.
    // Since the arrays are sorted, if any element differs, they are not equal.
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    // If the loop completes without finding any differences, the arrays are equal.
    return true;
  },
  areArraysEqualBy(arr1: unknown[], arr2: unknown[], key: string) {
    if ((arr1 == null && arr2 != null) || (arr1 != null && arr2 == null)) {
      return false;
    }
    if (arr1 == null && arr2 == null) {
      return true;
    }
    arr1 = arr1?.map((x) => x[key]);
    arr2 = arr2?.map((x) => x[key]);
    // First, check if the lengths are different. If so, they cannot be equal.
    if (arr1.length !== arr2.length) {
      return false;
    }
    // Iterate through the arrays, comparing elements at each index.
    // Since the arrays are sorted, if any element differs, they are not equal.
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }
    // If the loop completes without finding any differences, the arrays are equal.
    return true;
  },
  checkIsLive(item: Item) {
    if (item != null && item.sourceFileUrl == null && (item.source === 'LiveEncoder' || item.source === 'LiveStreamManager')) {
      return true;
    }
    return false;
  },
  calculateApproximateDurationFromManifest(playlistManifest: string) {
    // console.log('calculateApproximateDurationFromManifest', { playlistManifest });
    if (clientUtils.isNullOrWhitespace(playlistManifest)) {
      return 0;
    }
    let duration = 0;
    const regex = /#EXTINF:([0-9.]*),/gm;
    const matches = playlistManifest.match(regex);
    if (matches?.length > 0) {
      for (let i = 0; i < matches.length; i++) {
        const match = matches[i];
        const d = match.substring(8, match.length - 1);
        duration += parseFloat(d);
      }
    }
    return duration;
  },
  getExternalThumbnailIds(items: Item[]) {
    // console.log('getExternalThumbnailIds', { items });
    if (items == null) {
      return [];
    }
    return items
      .filter(
        (item) =>
          item.source === 'LiveEvent' ||
          item.data?.subtype === 'Series' ||
          item.data?.subtype === 'Season' ||
          item.data?.subtype === 'Extra' ||
          item.data?.subtype === 'Trailer'
      )
      .flatMap<string, Item>(
        (item: Item) => item.data?.linkedAssets?.find((x) => x.type === LinkedAssetType.Thumbnail)?.assetId ?? []
      );
  },
  resolveSymbolicClipTagName(item: Item) {
    // console.log('resolveSymbolicClipTagName', { item });
    if (item.data?.subtype != null && item.data?.subtype !== 'None' && item.data?.subtype !== 'Unknown') {
      return item.data?.subtype;
    }
    if (item.source === 'NestedChannel') {
      return 'N';
    }
    if (item.source === 'LiveEvent') {
      return 'E';
    }
    if (item.source === 'ClipGroup') {
      return 'G';
    }
    return null;
  },

  getClipIcon(item: Item) {
    if (item.data?.subtype != null && item.data?.subtype !== 'None' && item.data?.subtype !== 'Unknown') {
      return item.data?.subtype;
    }
    if (item.source === 'NestedChannel') {
      return nestedChannelIcon;
    }
    if (item.source === 'LiveEvent') {
      return liveEventIcon;
    }
    if (item.source === 'ClipGroup') {
      return clipGroupIcon;
    }
    if (item.source === 'LiveEncoder' || item.source === 'LiveStreamManager') {
      return liveProgramAssetIcon;
    }
    if (item.source === 'AdVariant') {
      return adVariantIcon;
    }
    if (item.source === 'Ingest' && item.schemaType === 'Graphics') {
      return transitionIcon;
    }
    if (item.source === 'Ingest' && item.schemaType === 'Image') {
      return imageIcon;
    }
    if (item.source === 'ExternalClip') {
      return externalClipIcon;
    }
    return null;
  },

  resolveThumbnailSrc(item: Item, defaultValue: string = '/static/images/thumbnail.png') {
    // console.log('resolveThumbnailSrc', { item, defaultValue });
    if (item && item.data && item.data.thumbnail && item.data.thumbnail.previewUri) {
      return item.data.thumbnail.previewUri;
    }
    return defaultValue;
  },

  /**
   *
   * @param parentClip The old clip item to copy over essential properties
   */
  getAssetVariant(parentClip: Item, userId: any, title: string, ouData: OU): Item {
    const newClip: Item = itemUtils.getDefaultClip(userId, ouData);
    const old = _.cloneDeep(parentClip);

    newClip.data.businessMetadata.title = title;
    newClip.data.businessMetadata.publicationDate = old.data.businessMetadata.publicationDate;
    newClip.data.accessSettings = old.data.accessSettings;
    newClip.data.displaySettings = old.data.displaySettings;
    newClip.data.businessMetadata.embargoDate = old.data.accessSettings.effectiveEmbargoDate;
    newClip.data.businessMetadata.expireDate = old.data.accessSettings.effectiveExpireDate;
    newClip.data.originalFileName = old.data.originalFileName;
    // copy other properties
    newClip.sourceFileUrl = old.sourceFileUrl;
    newClip.previewUri = old.previewUri;
    newClip.ouId = old.ouId;
    newClip.ouName = old.ouName;
    newClip.parentId = old.id;
    newClip.title = title;
    newClip.tags = old.tags;
    newClip.categories = old.categories;

    newClip.source = 'AdVariant';
    newClip.sourceType = 'AdVariant';

    // copy thumbnail
    newClip.data.thumbnail = itemUtils.copyThumbnail(old);

    // copy subtitles
    newClip.data.externalSubtitles = itemUtils.copyExternalSubtitles(old);

    newClip.timeStamps = [];
    newClip.data.timeStampMarkers = [{ markerType: 'AdInsertionPoints', timeStamps: [] }];
    // // edit existing ad insertion points if already present copied from parent clip
    // let adInsertionPointsIndex: number = _.findIndex(newClip.data.timeStampMarkers, (x) => x.markerType === 'AdInsertionPoints');
    // if (adInsertionPointsIndex === -1) {
    //   newClip.data.timeStampMarkers.push({ markerType: 'AdInsertionPoints', timeStamps: [] });
    //   adInsertionPointsIndex = newClip.data.timeStampMarkers.length - 1;
    // }
    // newClip.data.timeStampMarkers[adInsertionPointsIndex].timeStamps = timestamps;

    return newClip;
  },

  copyThumbnail(parentClip: Item): LinkedFileInfo {
    if (parentClip.data.thumbnail == null) {
      return null;
    }
    const element = _.cloneDeep(parentClip.data.thumbnail);
    element.s3Path = null;
    return element;
  },

  copyExternalSubtitles(parentClip: Item): LinkedFileInfo[] {
    if (parentClip.data.externalSubtitles == null || parentClip.data.externalSubtitles.length <= 0) {
      return [];
    }
    const externalSubtitles = [];
    for (let i = 0; i < parentClip.data.externalSubtitles.length; i++) {
      const element = _.cloneDeep(parentClip.data.externalSubtitles[i]);
      element.s3Path = null;
      externalSubtitles.push(element);
    }
    return externalSubtitles;
  },

  getDefaultExternalClip(userId: string, ouData: OU): Item {
    const asset = itemUtils.getDefaultClip(userId, ouData);
    // Add External Clip specific properties
    asset.applyAutomatedTransitions = false;
    asset.source = AssetSource.ExternalClip;
    asset.hlsFileUrl = null;
    if (asset.data.clipHLSData == null) {
      asset.data.clipHLSData = {};
    }
    asset.data.clipHLSData.externalHLSUrl = null;
    return asset;
  },

  /**
   *
   * @returns a new clip with bare bones properties set as Video schema type, and Ingest as source type
   */
  getDefaultClip(userId: string, ouData: OU): Item {
    return {
      entityType: FuelEntityType.Clip,
      streamingEngineClipId: null,
      clipVersion: 'v2',
      attributes: [],
      categories: [],
      priority: 0,
      conformStatus: null,
      tags: [],
      data: {
        originalFileName: null,
        businessMetadata: { title: '', publicationDate: dateUtils.getTimestamp(ouData.timezone) },
        technicalMetadata: null,
        timedText: null,
        thumbnail: null,
        videoProxy: null,
        ingestStatus: JobStatus.Unknown,
        timeStampMarkers: [],
        externalSubtitles: [],
        accessSettings: {
          effectiveAccessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
          accessLevel: ouData?.metaData?.defaultAccessLevel || AccessLevelType.Free,
          geographic: {
            geoblockingType: GeographicRestrictionType.NoGeoblocking,
            countries: [],
          },
          demographic: {
            ageGate: null,
          },
          temporal: {
            accessSchedules: [],
          },

          effectiveExpireDate: null,
          effectiveEmbargoDate: null,
        },
      },
      ouId: ouData.ouId,
      applyAutomatedTransitions: true,
      schemaName: SchemaType.Video,
      schemaType: SchemaType.Video,
      fileUri: null,
      hlsFileUrl: null,
      previewUri: null,
      sourceFileUrl: null,
      parentId: null,
      source: AssetSource.Ingest,
      publicationDate: dateUtils.getTimestamp(ouData.timezone),
      createdBy: userId,
      doNotDelete: false,
    };
  },

  isItemDraftSubClip(item: Item) {
    const isDraft = false;
    if (item && item.data && item.data.ingestStatus && item.data.ingestStatus === JobStatus.Unknown) return true;
    return isDraft;
  },

  // gets item frame rate else returns default frame rate (30 fps)
  getFrameRate(item: Item): number {
    let frameRate = DEFAULT_FRAME_RATE;
    if (
      item &&
      item.data &&
      item.data.technicalMetadata &&
      item.data.technicalMetadata.meta &&
      item.data.technicalMetadata.meta.videoTracks &&
      item.data.technicalMetadata.meta.videoTracks.length > 0
    ) {
      const firstVideoTrack = item.data.technicalMetadata.meta.videoTracks[0];
      if (firstVideoTrack.frameRate && +firstVideoTrack.frameRate > 0) {
        frameRate = +firstVideoTrack.frameRate;
      }
    }
    return frameRate;
  },

  getVideoProxyUrl(item: Item): string | null {
    // console.log('getVideoProxyUrl', { item });
    if (item.source === AssetSource.ExternalClip) {
      return item.hlsFileUrl;
    }
    let videoProxyUrl: string | null = null;
    const hlsFileUrl = item ? item.hlsFileUrl : null;
    const previewUri =
      item && item.data && item.data.videoProxy && item.data.videoProxy.previewUri ? item.data.videoProxy.previewUri : null;
    // have to avoid bad hlsFileUrl like 'Generating' see examples below
    if (hlsFileUrl && hlsFileUrl.startsWith('http')) {
      videoProxyUrl = hlsFileUrl;
    } else if (previewUri) {
      videoProxyUrl = previewUri;
    }
    // console.log('getVideoProxyUrl -> ', { videoProxyUrl });
    return videoProxyUrl;
  },

  /**
   * Returns a value that can be used as a key for a given string
   * by removing all the spaces and casting it to lower case.
   *
   * @param s The string to get a key from
   */
  getKey(s: string): string {
    return s.replace(/ /g, '').toLowerCase();
  },

  /**
   * Determines if a tag is actually a meta key value pair
   * @param tag
   * @returns
   */
  isMetaTag(tag: string) {
    // a meta tag is of the form KEY:VALUE where
    // KEY has ONLY ALPHA CHARACTERS (a-z and A-Z), UNDERSCORE, AND DASH ALLOWED, and
    // VALUE has at least one non-whitespace character for the value
    const regex = /^[a-zA-Z-_]+\s*:.*\S.*/;
    return regex.test(tag);
  },

  getMetaTagKeyValue(metaTag: string) {
    let key = null;
    let value = null;
    const index = metaTag.indexOf(':');
    if (index > -1) {
      key = this.getSafeMetaKey(metaTag.substring(0, index));
      value = this.getSafeMetaValue(metaTag.substring(index + 1));
    }
    return {
      key,
      value,
    };
  },

  /**
   * Rules for safe field names in elastic search and javascript:
   * must start with a letter
   * must not start with underscore "_", other special character, or a number
   * must not contain a space " " a point "." a comma "," or a hash mark "#"
   * field names must not be longer than 255
   * @param string
   * @param metaKey
   */
  getSafeMetaKey(metaKey: string) {
    let safeMetaKey = metaKey;
    safeMetaKey = safeMetaKey.toLowerCase(); // make everything lowercase
    safeMetaKey = safeMetaKey.trim(); // trim all leading and trailing whitespace
    safeMetaKey = safeMetaKey.replace(/[^a-z-_]/g, ''); // remove everything that is not a letter, underscore or dash, including spaces
    return safeMetaKey;
  },

  getSafeMetaValue(value: string) {
    return value.trim();
  },

  getSafeMetaTagString(s: string) {
    const { key, value } = this.getMetaTagKeyValue(s);
    return `${key}:${value}`;
  },

  getSafeTagString(value: string) {
    return value != null ? value.trim().toLowerCase() : null;
  },
  onSingleTagFieldBlur(event, existingTags) {
    const { value } = event.target;
    const customFieldName = event && event.target && event.target.hasAttribute('name') ? event.target.getAttribute('name') : null;
    const fieldTags: string[] = [];
    if (value) {
      fieldTags.push(`${customFieldName}:${value}`);
    }
    const newTags = [];
    for (let i = 0; i < existingTags.length; i++) {
      const t = existingTags[i];
      if (itemUtils.isMetaTag(t)) {
        const { key, value } = itemUtils.getMetaTagKeyValue(t);
        if (key === customFieldName) {
          if (fieldTags.indexOf(t) > -1) newTags.push(t);
        } else {
          newTags.push(t);
        }
      } else {
        newTags.push(t);
      }
    }
    for (let i = 0; i < fieldTags.length; i++) {
      const f = fieldTags[i];
      if (newTags.indexOf(f) === -1) newTags.push(f);
    }
    return newTags;
  },

  isSingleValueTagField(key) {
    if (
      key === 'rating-mpaa' ||
      key === 'rating-bbfc' ||
      key === 'episode-xmltv' ||
      key === 'episode-onscreen' ||
      key === 'season' ||
      key === 'series' ||
      key === 'episode' ||
      key === 'us-parental-ratings' ||
      key === 'network-value' ||
      key === 'program-type' ||
      key === 'original-air-date' ||
      key === 'european-fsk-rating' ||
      key === 'tmsid-series' ||
      key === 'tmsid-episode'
    ) {
      return true;
    }
    return false;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFirstOrDefault(array: any[] | string | null | undefined, defaultValue: any = null) {
    return array?.length > 0 ? array[0] : defaultValue;
  },

  // Used to extract tags into commonBusinessMetadata fields
  getFullTagsFromCommonMetadata(common: CommonBusinessMetadata) {
    // console.log('common', common);
    let tags: string[] = [];
    tags = itemUtils.pushAll(tags, common.tags);
    tags = itemUtils.pushAll(tags, common.cast, 'actor');
    tags = itemUtils.pushAll(tags, common.producer, 'producer');
    tags = itemUtils.pushAll(tags, common.director, 'director');
    tags = itemUtils.pushSingle(tags, common.mpaaRating, 'rating-mpaa');
    tags = itemUtils.pushSingle(tags, common.bbfcRating, 'rating-bbfc');
    tags = itemUtils.pushSingle(tags, common.releaseYear, 'release-year');
    tags = itemUtils.pushAll(tags, common.genre, 'genre');
    tags = itemUtils.pushAll(tags, common.series, 'series');
    tags = itemUtils.pushAll(tags, common.season, 'season');
    tags = itemUtils.pushSingle(tags, common.episode, 'episode');
    tags = itemUtils.pushSingle(tags, common.episodeXmltv, 'episode-xmltv');
    tags = itemUtils.pushSingle(tags, common.episodeOnscreen, 'episode-onscreen');
    tags = itemUtils.pushSingle(tags, common.europeanFskRating, 'european-fsk-rating');
    tags = itemUtils.pushSingle(tags, common.usParentalRatings, 'us-parental-ratings');
    tags = itemUtils.pushAll(tags, common.leagueName, 'league-name');
    tags = itemUtils.pushAll(tags, common.teamName, 'team-name');
    tags = itemUtils.pushSingle(tags, common.originalAirDate, 'original-air-date');
    tags = itemUtils.pushSingle(tags, common.networkValue, 'network-value');
    tags = itemUtils.pushSingle(tags, common.tmsidEpisode, 'tmsid-episode');
    tags = itemUtils.pushSingle(tags, common.tmsidSeries, 'tmsid-series');
    tags = itemUtils.pushSingle(tags, common.programType, 'program-type');
    return tags;
  },

  pushAll(tags: string[], arr2: string[], metaKey: string = null) {
    if (arr2 != null && arr2.length > 0) {
      if (!clientUtils.isNullOrWhitespace(metaKey)) {
        tags.push(...arr2.map((x) => `${metaKey}:${x}`));
      } else {
        tags.push(...arr2);
      }
    }
    return tags;
  },

  pushSingle(tags: string[], value: string, metaKey: string = null) {
    if (!clientUtils.isNullOrWhitespace(value)) {
      if (!clientUtils.isNullOrWhitespace(metaKey)) {
        tags.push(`${metaKey}:${value}`);
      } else {
        tags.push(value);
      }
    }
    return tags;
  },

  // Used to extract tags into commonBusinessMetadata fields
  extractCommonTags(tags: string[]) {
    const myState: CommonTagKeys = {
      cast: [],
      producer: [],
      director: [],
      ratingMpaa: [],
      ratingBbfc: [],
      releaseYear: '',
      genre: [],
      episodeXmltv: '',
      episodeOnscreen: '',
      usParentalRatings: '',
      europeanFskRating: '',
      networkValue: '',
      programType: '',
      originalAirDate: '',
      teamName: [],
      leagueName: [],
      tmsidEpisode: '',
      tmsidSeries: '',
      season: [],
      episode: '',
      series: [],
      tags: [],
    };

    const mpaa = new Set(['G', 'PG', 'PG-13', 'TV-Y', 'TV-Y7', 'TV-G', 'TV-PG', 'TV-14', 'TV-MA', 'NC-17', 'R']);
    const bbfc = new Set(['U', 'PG', '12', '12A', '15', '18', 'R18']);

    const keyMap: Record<string, keyof CommonTagKeys> = {
      actor: 'cast',
      cast: 'cast',
      producer: 'producer',
      director: 'director',
      'rating-mpaa': 'ratingMpaa',
      'rating-bbfc': 'ratingBbfc',
      'release-year': 'releaseYear',
      genre: 'genre',
      'episode-xmltv': 'episodeXmltv',
      'episode-onscreen': 'episodeOnscreen',
      'us-parental-ratings': 'usParentalRatings',
      'european-fsk-rating': 'europeanFskRating',
      'network-value': 'networkValue',
      'program-type': 'programType',
      'original-air-date': 'originalAirDate',
      'team-name': 'teamName',
      'league-name': 'leagueName',
      'tmsid-episode': 'tmsidEpisode',
      'tmsid-series': 'tmsidSeries',
      season: 'season',
      episode: 'episode',
      series: 'series',
      tags: 'tags',
    };

    tags.forEach((tag) => {
      if (!itemUtils.isMetaTag(tag)) {
        myState.tags.push(tag);
        return;
      }

      const { key, value } = itemUtils.getMetaTagKeyValue(tag);
      const mappedKey = keyMap[key];

      if (!mappedKey) {
        myState.tags.push(tag);
        return;
      }

      if (mappedKey === 'ratingMpaa' && mpaa.has(value)) {
        myState[mappedKey].push(value);
      } else if (mappedKey === 'ratingBbfc' && bbfc.has(value)) {
        myState[mappedKey].push(value);
      } else if (Array.isArray(myState[mappedKey])) {
        myState[mappedKey].push(value);
      } else {
        myState[mappedKey] = value;
      }
    });

    return myState;
  },

  extractCustomAttributesFromTags(tags, currentState) {
    const myState = _.cloneDeep(currentState);
    // console.log('refreshLongFormFields', tags);
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const fieldKey in myState) {
      // console.log('refreshLongFormFields fieldKey', fieldKey);
      const myvalues = [];
      if (tags && tags.length > 0) {
        for (let i = 0; i < tags.length; i++) {
          const tag = tags[i];
          if (itemUtils.isMetaTag(tag)) {
            const { key, value } = itemUtils.getMetaTagKeyValue(tag);
            if (key === fieldKey || (fieldKey === 'actor' && key === 'cast')) {
              myvalues.push(value);
            }
          }
        }
      }

      let fieldValue = _.join(myvalues, ', ');
      // just take the first tag for these special one liner metatags
      if (myvalues.length > 1 && itemUtils.isSingleValueTagField(fieldKey)) {
        fieldValue = myvalues[0];
      }
      myState[fieldKey] = fieldValue;
    }
    return myState;
  },
  onMultipleTagFieldBlur(event, existingTags) {
    // console.log('onCustomTagFieldBlur', { event });
    const val = event.target.value;
    const customFieldName = event && event.target && event.target.hasAttribute('name') ? event.target.getAttribute('name') : null;
    // console.log('onCustomTagFieldBlur', { customFieldName, val });
    const fieldTags: string[] = [];
    if (val) {
      const split = val.split(',');
      for (let i = 0; i < split.length; i++) {
        let s = split[i];
        if (s) {
          s = itemUtils.getSafeMetaValue(s);
          if (s.length > 0) {
            fieldTags.push(s);
          }
        }
      }
    }
    // console.log('onCustomTagFieldBlur', fieldTags);
    const newTags: string[] = [];
    for (let i = 0; i < existingTags.length; i++) {
      const t = existingTags[i];
      if (itemUtils.isMetaTag(t)) {
        const { key, value } = itemUtils.getMetaTagKeyValue(t);
        if (customFieldName === 'actor') {
          if (key === 'actor' || key === 'cast') {
            if (
              fieldTags.indexOf(value) > -1 &&
              newTags.indexOf(`actor:${value}`) === -1 &&
              newTags.indexOf(`cast:${value}`) === -1
            ) {
              newTags.push(t);
            }
          } else {
            newTags.push(t);
          }
        } else if (key === customFieldName) {
          if (fieldTags.indexOf(value) > -1) newTags.push(t);
        } else {
          newTags.push(t);
        }
      } else {
        newTags.push(t);
      }
    }
    for (let i = 0; i < fieldTags.length; i++) {
      const f = `${customFieldName}:${fieldTags[i]}`;
      if (customFieldName === 'actor') {
        if (newTags.indexOf(f) === -1 && newTags.indexOf(`cast:${fieldTags[i]}`) === -1) newTags.push(f);
      } else if (newTags.indexOf(f) === -1) newTags.push(f);
    }
    return newTags;
  },

  cleanupTags(mySelectedTags: string[]) {
    // console.log('cleanupTags mySelectedTags', mySelectedTags);
    let uniqTags: string[] = [];
    const tagKeys: string[] = []; // all lower case keys for metatag
    const selectedTags = _.clone(mySelectedTags, true);
    for (let i = 0; i < selectedTags.length; i++) {
      const t = selectedTags[i];
      const tag = this.isMetaTag(t) ? this.getSafeMetaTagString(t) : this.getSafeTagString(t);
      if (tag) {
        uniqTags.push(tag);
      }
    }
    uniqTags = _.uniq(uniqTags);
    for (let i = 0; i < uniqTags.length; i++) {
      tagKeys.push(uniqTags[i].toLowerCase());
    }

    // console.log('cleanupTags uniqTags, tagKeys', uniqTags, tagKeys);

    let removeValFromIndex: number[] = [];
    for (let i = 0; i < uniqTags.length; i++) {
      if (!_.includes(removeValFromIndex, i)) {
        const allMatches = [];
        for (let j = i; j < tagKeys.length; j++) {
          if (tagKeys[i] === tagKeys[j]) {
            allMatches.push({ index: j, tag: uniqTags[j] });
          }
        }
        if (allMatches.length > 1) {
          // defer to the first existing meta tag that matches on a case-insensitive basis
          for (let k = 1; k < allMatches.length; k++) {
            removeValFromIndex.push(allMatches[k].index);
          }

          // console.log('mixmatch detected', uniqTags[i], allMatches);
          // _.map(allMatches, (m) => (m.uppercaseCount = m.tag.replace(/[^A-Z]/g, '').length));
          // allMatches = _.orderBy(allMatches, ['uppercaseCount'], ['desc']); // the word with the most uppercase letters wins
          // for (let k = 1; k < allMatches.length; k++) {
          //   removeValFromIndex.push(allMatches[k].index);
          // }
        }
      }
    }

    removeValFromIndex = _.uniq(removeValFromIndex);
    for (let i = removeValFromIndex.length - 1; i >= 0; i--) uniqTags.splice(removeValFromIndex[i], 1);

    // console.log('cleanupTags', uniqTags);
    return uniqTags;
  },

  recalcAttributes(
    categories: Category[],
    attributes: ChannelAttribute[],
    mySelectedTags: string[],
    selectedCategories: string[]
  ) {
    // console.log('recalcAttributes', { categories, attributes, mySelectedTags, selectedCategories });
    let tags = [];
    const selectedTags = _.clone(mySelectedTags, true);
    for (let i = 0; i < selectedTags.length; i++) {
      const t = selectedTags[i];
      const tag = this.isMetaTag(t) ? this.getSafeMetaTagString(t) : this.getSafeTagString(t);
      if (tag != null) tags.push(tag);
    }
    tags = _.uniq(tags);
    const categoryAttrLookup = new Map<string, ChannelAttribute>();
    const tagAttrLookup = new Map<string, ChannelAttribute>();
    const newCategoryAttrs: ChannelAttribute[] = selectedCategories.map((categoryId) => {
      const category = _.find(categories, (x) => x.id === categoryId);
      if (category === undefined) throw new Error(`could not map categoryId: ${categoryId} to an existing category!`);
      const key = this.getKey(category.name);
      const existing = categoryAttrLookup.get(key);
      if (existing !== undefined) {
        return existing;
      }
      const newAttr: ChannelAttribute = {
        key,
        name: category.name,
        score: 0,
        type: ChannelAttributeType.CATEGORY,
      };
      return newAttr;
    });
    const newTagAttrs: ChannelAttribute[] = tags.map((tag) => {
      let name = tag;
      const key = this.getKey(tag);
      const isMeta = this.isMetaTag(tag);
      if (isMeta) {
        name = this.getSafeMetaTagString(tag);
      }
      const existing = tagAttrLookup.get(key);
      if (existing !== undefined) {
        existing.isMeta = this.isMetaTag(existing.key);
        return existing;
      }
      const newAttr: ChannelAttribute = {
        key,
        name,
        isMeta,
        type: 'tag',
        score: 0,
      };
      return newAttr;
    });
    const newAttributes = newCategoryAttrs.concat(newTagAttrs);
    return newAttributes;
  },

  isItemEmbargoedOrExpired(item: Item) {
    if (item && item.data && item.data.businessMetadata) {
      // embargo
      if (item.embargoDate && new Date(item.embargoDate) > new Date()) {
        return true;
      }
      // expired
      if (item.expireDate && new Date(item.expireDate) <= new Date()) {
        return true;
      }
    }
    return false;
  },

  getOrderedVariantsList(channelVariants: string[], supportedVariants: ISupportedVariantList) {
    // console.log('getOrderedVariantsList', { channelVariants, supportedVariants });
    if (channelVariants == null) {
      return null;
    }
    return _.map(
      _.orderBy(
        _.map(channelVariants, (x) => supportedVariants[x]),
        ['SegmentLength', 'FrameRate', 'BitrateKbps', 'Height'],
        ['desc', 'desc', 'asc', 'asc']
      ),
      'VariantCode'
    );
  },

  getVariantsStatusAreNotCompleted(variantStatuses) {
    const variantsAreNotCompleted: string[] = [];
    for (const key in variantStatuses) {
      // check if the property/key is defined in the object itself, not in parent
      if (variantStatuses.hasOwnProperty(key) && variantStatuses[key] !== 'Completed') {
        variantsAreNotCompleted.push(key);
      }
    }
    return variantsAreNotCompleted;
  },
  getSupportedVariants(allVariants) {
    const toPascalCase = (string) => {
      return string.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()).replace(/^[a-z]/, (letter) => letter.toUpperCase());
    };
    const convertKeysToPascalCase = (obj) => {
      return _.mapKeys(obj, (value, key) => toPascalCase(key));
    };
    const supportedVariants = allVariants.reduce((dict, item) => {
      dict[item.variantCode] = convertKeysToPascalCase(item);
      return dict;
    }, {});
    return supportedVariants;
  },

  /**
   * Returns an object with { error: boolean, status: string, variantCodes: string[], message: string }
   * @param item The item with variant codes to check against the provided list
   * @param variantCodes An ordered list of desired variants, with the first being required
   * @param supportedVariants A list of supported variants (optional)
   * @param checkClipStatusMarker Boolean flag to check clip status
   * @param channelTargetDuration Target duration for the channel
   * @param channelFrameRate Frame rate for the channel
   */
  checkLowestVariantCodes(
    item: Item,
    variantCodes: string[],
    supportedVariants: ISupportedVariantList[] = [],
    checkClipStatusMarker: boolean = false,
    channelTargetDuration: number,
    channelFrameRate: number
  ) {
    // console.log('checkLowestVariantCodes', {
    //   item,
    //   variantCodes,
    //   supportedVariants,
    //   checkClipStatusMarker,
    //   channelTargetDuration,
    //   channelFrameRate,
    // });
    const defaultResponse = { error: false, status: 'success', variantCodes: [], message: null };

    if (!item || !variantCodes?.length) {
      return defaultResponse; // Legacy channels pass validation
    }

    if (item.source === 'ExternalClip') {
      return defaultResponse;
    }

    if (item.source !== 'NestedChannel' && !checkClipStatusMarker) {
      return defaultResponse; // Skip validation for non-nested channel clips
    }

    const clipData = item?.data?.clipHLSData;
    const clipVariants = clipData?.variants?.length > 0 ? clipData?.variants : [];
    const clipVariantsCodes = clipVariants?.length > 0 ? clipVariants?.map((v) => v.variantCode) : [];
    const clipVariantsNotCompleted = itemUtils.getVariantsStatusAreNotCompleted(clipData.variantStatuses);
    const orderedVariantCodes = itemUtils.getOrderedVariantsList(variantCodes, supportedVariants);
    const missingVariants = _.difference(orderedVariantCodes, clipVariantsCodes);
    const clipTargetDuration = clipVariants[0]?.segmentLength || null;
    const clipFrameRate = clipVariants[0]?.frameRate || null;

    const getNestedChannelErrorMessage = (channelName) =>
      `The nested channel ${channelName}'s target duration and/or frame rate do not match the parent channel, so it will not playout successfully. Please reach out to your SE or support to make the necessary adjustments.`;

    if (item.source === 'NestedChannel') {
      if (channelTargetDuration !== clipTargetDuration || channelFrameRate !== clipFrameRate) {
        return {
          error: true,
          status: 'danger',
          variantCodes: missingVariants,
          message: getNestedChannelErrorMessage(item.title),
        };
      }
    }

    if (['NestedChannel', 'ClipGroup'].includes(item.source)) {
      if (!clipVariantsCodes.includes(orderedVariantCodes[0])) {
        return { error: false, status: 'danger', variantCodes: missingVariants, message: '' };
      }
    } else if (
      !clipVariantsCodes.includes(orderedVariantCodes[0]) ||
      (clipData.variantStatuses[orderedVariantCodes[0]] !== 'Completed' && checkClipStatusMarker)
    ) {
      return {
        error: false,
        status: 'danger',
        variantCodes: missingVariants,
        message: '',
      };
    }

    if (
      !itemUtils.arrayContainsArray(orderedVariantCodes, clipVariantsCodes) ||
      (clipVariantsNotCompleted.length > 0 && checkClipStatusMarker)
    ) {
      return {
        error: false,
        status: 'warning',
        variantCodes: missingVariants,
        message: '',
      };
    }

    return defaultResponse;
  },
  /**
   * Returns TRUE if the first specified array contains all elements
   * from the second one. FALSE otherwise.
   *
   * @param {array} superset
   * @param {array} subset
   *
   * @returns {boolean}
   */
  arrayContainsArray(superset, subset) {
    for (let i = 0; i < superset.length; i++) {
      const element = superset[i];
      if (!_.includes(subset, element)) {
        return false;
      }
    }
    return true;
  },

  // cleans up timestamps by removing duplicates, and any values greater or less than the given lower and upperBound
  cleanseTimeStamps(timestamps: number[], lowerBound: number | null, upperBound: number | null) {
    // console.log('cleanseTimeStamps', { timestamps, lowerBound, upperBound });
    if (timestamps == null) {
      return null;
    }
    if (timestamps.length <= 0) {
      return timestamps;
    }
    const uniqTimeStamps = _.uniq(_.filter(timestamps, (el) => !_.isNull(el)));
    const cleansedTimestamps: number[] = [];
    for (let i = 0; i < uniqTimeStamps.length; i++) {
      if (lowerBound != null && uniqTimeStamps[i] < lowerBound) {
        continue;
      }
      if (upperBound != null && uniqTimeStamps[i] > upperBound) {
        continue;
      }
      cleansedTimestamps.push(uniqTimeStamps[i]);
    }
    // console.log('cleanseTimeStamps', { cleansedTimestamps });
    return cleansedTimestamps;
  },

  getFilteredCategories(categories: Category[], itemCategories: Category[] = null) {
    // console.log('getFilteredCategories', { categories, itemCategories });
    if (categories == null || categories.length === 0) {
      return [];
    }
    const filteredCategories: Category[] = categories.filter(
      (category: Category) => category.isDeleted === false && category.enabled === true
    );

    if (itemCategories != null && itemCategories.length > 0) {
      for (const itemCategory of itemCategories) {
        if (filteredCategories.findIndex((x) => x.id === itemCategory.id) === -1) {
          const category = categories.find((c) => c.id === itemCategory.id);
          if (category) {
            filteredCategories.push(category);
          } else {
            // create a fake category
            filteredCategories.push(itemCategory);
          }
        }
      }
    }
    return filteredCategories;
  },

  getDefaultSelectedCategories(
    allowMultiCategorySelect: boolean,
    filteredCategories: Category[],
    itemCategories: Category[] | Category
  ) {
    // console.log('getDefaultSelectedCategories', { allowMultiCategorySelect, filteredCategories, itemCategories });
    if (allowMultiCategorySelect) {
      if (_.isArray(itemCategories)) {
        return filteredCategories.filter(
          (x) => itemCategories?.map((c: Category) => c.id)?.findIndex((id: string) => id === x.id) > -1
        );
      }
      if (_.isObject(itemCategories)) {
        return filteredCategories.filter((x) => x.id === itemCategories.id);
      }
      return [];
    }
    if (_.isArray(itemCategories) && itemCategories.length > 0) {
      return filteredCategories.find((x) => x.id === itemCategories[0].id);
    }
    if (_.isObject(itemCategories)) {
      return filteredCategories.find((x) => x.id === itemCategories.id);
    }
    return '';
  },

  ensureArray(value: any) {
    // console.log('ensureArray', { value });
    if (_.isArray(value)) {
      return value;
    }
    if (value != null && value !== '') {
      return [value];
    }
    return [];
  },
};

export default itemUtils;
